/**
 * El paso donde el dueño le enseña a su bot **y lo prueba** (GKTIBO-124).
 *
 * ## Qué cambió el 2026-09-23
 *
 * Antes enseñar y probar eran dos lugares distintos, y la queja del PO fue
 * exacta: *«parece que se saltara de un lado al otro»*. Ahora es una sola
 * pantalla con **una sola caja de conversación** que cambia de interlocutor:
 * primero Tibo entrevista, y cuando no quedan preguntas, esa misma caja pasa a
 * ser su bot.
 *
 * El panel de la izquierda no se desmonta en el medio. Ver lo que su bot sabe
 * mientras lo prueba es lo que cierra el circuito: contesta mal → mira la
 * sección → corrige → vuelve a preguntar.
 *
 * ## Qué cambió el 2026-09-19
 *
 * **El editor de Markdown se fue** (GKTIBO-82). La regla del PO es que el
 * dueño no toca el archivo nunca —no es técnico— y que el conocimiento lo
 * construye siempre un asistente.
 *
 * ## El gate
 *
 * No se puede seguir sin haber probado al menos una vez. La marca es del
 * servidor (`probado`), no del navegador; lo local sólo habilita el botón
 * apenas el bot contesta, para no tener que volver a consultar el estado en
 * medio de la conversación.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api.ts';
import { ConQuienHablas } from './ConQuienHablas';
import { Conversacion } from './Conversacion';
import { PanelDeConocimiento } from './PanelDeConocimiento';
import { ProbarElBot } from './ProbarElBot';
import { SeccionDeLaPagina } from './SeccionDeLaPagina';
import { textos } from './textos';

interface PasoDeConocimientoProps {
  tenantId: string;
  /** El nombre que el dueño escribió al registrarse. */
  nombre: string;
  /** Lo que dice el servidor: ¿ya habló con su bot? */
  probado: boolean;
  /**
   * Tocó «Continuar» y el servidor ya lo sabe.
   *
   * Puede devolver una promesa, y si la devuelve **hay que esperarla**: el
   * botón se apaga mientras la mutación corre, y ése es el único guard
   * contra el doble toque. `onSuccess: () => onSeguir()` la propaga; con una
   * llave de más —`() => { onSeguir(); }`— la mutación termina antes de que
   * el servidor conteste y el botón vuelve a aceptar toques (ronda 2, FU-4).
   */
  onSeguir: () => void | Promise<void>;
  /**
   * Por qué el paso todavía no está completo aunque ya haya probado (lo
   * calcula `derivarEstado`). Sin mostrarlo, el dueño veía «ya lo probaste,
   * continuar», tocaba, y no pasaba nada visible.
   */
  falta?: string;
}

export function PasoDeConocimiento({
  tenantId,
  nombre,
  probado,
  onSeguir,
  falta,
}: PasoDeConocimientoProps) {
  const queryClient = useQueryClient();
  /** Habla con su bot en ESTA sesión. Sólo habilita el botón; el gate es del servidor. */
  const [proboAca, setProboAca] = useState(false);

  const {
    isError,
    data: kbActual,
    refetch: refetchKb,
  } = useQuery({
    queryKey: ['tenant-kb-existe', tenantId],
    queryFn: () => api.tenants.getKb(tenantId),
    retry: false,
  });
  const kb = kbActual?.markdown;

  /**
   * ¿Quedan preguntas? Lo decide el SERVIDOR.
   *
   * Calcularlo acá sería un segundo cálculo de la agenda que tarde o temprano
   * dice algo distinto del primero — y el primero es el que guarda.
   */
  const apertura = useQuery({
    queryKey: ['apertura-conversacion', tenantId],
    queryFn: () => api.tenants.aperturaDeConversacion(tenantId),
  });
  const entrevistaTerminada = apertura.data?.temaEnCurso === null;

  useEffect(() => {
    if (!isError) return;
    void api.tenants
      .initKb(tenantId, { nombre })
      .catch(() => undefined)
      .finally(() => {
        void queryClient.invalidateQueries({ queryKey: ['tenant-kb', tenantId] });
        void queryClient.invalidateQueries({ queryKey: ['tenant-kb-existe', tenantId] });
        void queryClient.invalidateQueries({ queryKey: ['conocimiento-temas', tenantId] });
      });
  }, [isError, tenantId, nombre, queryClient]);

  /**
   * Lo que se escribió hay que volver a leerlo del servidor, no suponerlo.
   *
   * `apertura-conversacion` también: es lo que hace que la caja pase a ser el
   * bot cuando se contesta la última pregunta. Sin invalidarla, el dueño
   * terminaba la entrevista y seguía viendo a Tibo esperando.
   */
  function seActualizo() {
    void refetchKb();
    void queryClient.invalidateQueries({ queryKey: ['conocimiento-temas', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['apertura-conversacion', tenantId] });
    // Y el estado del alta. Sin esto, el dueño terminaba la entrevista, veía
    // la lista en verde y no le aparecía nada para seguir: el estado seguía
    // siendo el de cuando abrió la página. Recargar lo arreglaba — pero para
    // saber que hay que recargar hay que sospechar que hay un bug.
    void queryClient.invalidateQueries({ queryKey: ['onboarding-status', tenantId] });
  }

  /**
   * Continuar: se marca en el servidor ANTES de avanzar.
   *
   * Si se marcara sólo al conversar, un fallo de escritura dejaría al dueño
   * tocando un botón que no hace nada. Acá el fallo se ve y se puede
   * reintentar.
   */
  const seguir = useMutation({
    mutationFn: () => api.tenants.yaProbe(tenantId),
    onSuccess: () => onSeguir(),
  });

  const puedeSeguir = probado || proboAca;

  return (
    <section className="alta-paso">
      <h2 className="card-title">{textos.conocimiento.titulo}</h2>

      <div className="alta-conocimiento-ayuda" data-testid="alta-conocimiento-ayuda">
        {/* El orden importa: primero la consecuencia —sin esto no sale en
            vivo—, después lo que más valor agrega. */}
        <p>{textos.conocimiento.porQue}</p>
        <p>
          <strong>{textos.conocimiento.loQueMasImporta}</strong>
        </p>
        <p>{textos.conocimiento.dejamosEmpezado}</p>
      </div>

      {/* Dos columnas: a la izquierda lo que el bot sabe, a la derecha la
          conversación. El progreso tiene que estar a la vista MIENTRAS se
          conversa — no saber cuánto falta es donde se abandona un alta.

          En celular se apila, y la conversación va PRIMERO: es donde se
          trabaja. */}
      <div className="alta-dos-columnas">
        <aside className="alta-columna-saber">
          <PanelDeConocimiento tenantId={tenantId} />
        </aside>

        <div className="alta-columna-conversar">
          {/* Antes de preguntar: si su página contesta, escribe menos. */}
          <SeccionDeLaPagina tenantId={tenantId} />

          {kb && (
            <>
              <ConQuienHablas quien={entrevistaTerminada ? 'bot' : 'tibo'} negocio={nombre} />

              {/* LA MISMA CAJA, dos interlocutores. Lo que hace que esto se
                  sienta un paso y no dos es que el panel de la izquierda no se
                  desmonta en el medio. */}
              {entrevistaTerminada ? (
                <ProbarElBot tenantId={tenantId} onProbado={() => setProboAca(true)} />
              ) : (
                <Conversacion tenantId={tenantId} nombre={nombre} onGuardado={seActualizo} />
              )}
            </>
          )}
        </div>
      </div>

      {/* El gate. Antes de probar, el botón está apagado y dice por qué. */}
      {entrevistaTerminada && (
        <footer className="alta-paso-pie" data-testid="alta-paso-pie">
          <p className="alta-paso-pie-texto">
            {puedeSeguir
              ? (falta ?? textos.conocimiento.yaProbaste)
              : textos.conocimiento.probaloPrimero}
          </p>
          <Button
            type="button"
            disabled={!puedeSeguir || seguir.isPending}
            onClick={() => seguir.mutate()}
          >
            {textos.conocimiento.continuar}
          </Button>
          {seguir.isError && (
            <p className="alta-chat-error" role="alert">
              {textos.conocimiento.noSePudoSeguir}
            </p>
          )}
        </footer>
      )}
    </section>
  );
}
